import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { db, collections } from '../../firebase.marketplace';
import { collection, query, where, getDocs, Timestamp, orderBy, limit } from 'firebase/firestore';
import ProductCard from './ProductCard';
import FilterPanel from './FilterPanel';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  FaSearch, 
  FaFilter, 
  FaSortAmountDown, 
  FaSortAmountUp, 
  FaThLarge, 
  FaList, 
  FaExclamationTriangle, 
  FaShoppingBag 
} from 'react-icons/fa';

const Container = styled.div`
  padding: 2.5rem 2rem;
  max-width: 1400px;
  margin: 0 auto;
  
  @media (max-width: 768px) {
    padding: 2rem 1.5rem;
  }
  
  @media (max-width: 480px) {
    padding: 1.5rem 1rem;
  }
`;

const Header = styled.div`
  margin-bottom: 2.5rem;
  position: relative;
  
  &::after {
    content: '';
    position: absolute;
    bottom: -1.2rem;
    left: 0;
    width: 60px;
    height: 3px;
    background: linear-gradient(90deg, #4a90e2, transparent);
    border-radius: 3px;
  }
  
  @media (max-width: 768px) {
    margin-bottom: 2rem;
  }
`;

const Title = styled.h1`
  color: white;
  font-size: 2.2rem;
  margin-bottom: 1rem;
  font-weight: 700;
  letter-spacing: 0.02em;
  background: linear-gradient(90deg, #fff, #e0e0e0);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  
  @media (max-width: 768px) {
    font-size: 1.8rem;
  }
  
  @media (max-width: 480px) {
    font-size: 1.6rem;
  }
`;

const Description = styled.p`
  color: rgba(255, 255, 255, 0.7);
  margin-bottom: 2rem;
  font-size: 1.05rem;
  line-height: 1.6;
  max-width: 800px;
  
  @media (max-width: 768px) {
    font-size: 1rem;
  }
`;

const ControlsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 1.2rem;
  margin-bottom: 2.5rem;
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.05) 0%, rgba(255, 255, 255, 0.02) 100%);
  padding: 1.5rem;
  border-radius: 16px;
  border: 1px solid rgba(255, 255, 255, 0.05);
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.1);
  backdrop-filter: blur(10px);
  
  @media (max-width: 768px) {
    flex-direction: column;
    padding: 1.2rem;
    gap: 1rem;
    margin-bottom: 2rem;
  }
`;

const SearchContainer = styled.div`
  flex: 1;
  position: relative;
  min-width: 300px;
  
  @media (max-width: 768px) {
    min-width: 100%;
  }
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 1rem 1.2rem 1rem 3rem;
  background: rgba(255, 255, 255, 0.07);
  border: 1px solid rgba(255, 255, 255, 0.15);
  border-radius: 12px;
  color: white;
  font-size: 1.05rem;
  transition: all 0.3s ease;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  
  &:focus {
    outline: none;
    border-color: #4a90e2;
    background: rgba(74, 144, 226, 0.05);
    box-shadow: 0 0 0 3px rgba(74, 144, 226, 0.15), 0 4px 12px rgba(0, 0, 0, 0.1);
  }
  
  &::placeholder {
    color: rgba(255, 255, 255, 0.4);
    font-weight: 400;
  }
  
  @media (max-width: 768px) {
    padding: 0.9rem 1rem 0.9rem 2.8rem;
    font-size: 1rem;
    border-radius: 10px;
  }
`;

const SearchIcon = styled.div`
  position: absolute;
  left: 1.2rem;
  top: 50%;
  transform: translateY(-50%);
  color: rgba(255, 255, 255, 0.5);
  font-size: 1.2rem;
  transition: all 0.3s ease;
  
  @media (max-width: 768px) {
    left: 1rem;
    font-size: 1.1rem;
  }
`;

const FilterContainer = styled.div`
  display: flex;
  gap: 0.7rem;
  flex-wrap: wrap;
  align-items: center;
  
  @media (max-width: 768px) {
    gap: 0.5rem;
  }
`;

const FilterButton = styled(motion.button)`
  padding: 0.7rem 1.2rem;
  background: ${props => props.active ? 'linear-gradient(90deg, #4a90e2, #357abd)' : 'rgba(255, 255, 255, 0.07)'};
  border: 1px solid ${props => props.active ? '#4a90e2' : 'rgba(255, 255, 255, 0.15)'};
  color: ${props => props.active ? 'white' : 'rgba(255, 255, 255, 0.8)'};
  border-radius: 12px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.6rem;
  font-size: 0.95rem;
  white-space: nowrap;
  transition: all 0.3s ease;
  box-shadow: ${props => props.active ? '0 4px 12px rgba(74, 144, 226, 0.3)' : 'none'};
  font-weight: ${props => props.active ? '600' : '500'};
  
  svg {
    color: ${props => props.active ? 'white' : '#4a90e2'};
    font-size: 0.9rem;
  }
  
  &:hover {
    background: ${props => props.active ? 'linear-gradient(90deg, #357abd, #2868a9)' : 'rgba(255, 255, 255, 0.12)'};
    border-color: ${props => props.active ? '#357abd' : 'rgba(255, 255, 255, 0.25)'};
    color: white;
    box-shadow: ${props => props.active ? '0 6px 15px rgba(74, 144, 226, 0.35)' : '0 4px 10px rgba(0, 0, 0, 0.1)'};
    
    svg {
      color: white;
    }
  }
  
  @media (max-width: 768px) {
    padding: 0.6rem 1rem;
    font-size: 0.9rem;
    border-radius: 10px;
  }
`;

const SortButton = styled(FilterButton)`
  margin-left: auto;
  background: rgba(74, 144, 226, 0.1);
  border: 1px solid rgba(74, 144, 226, 0.2);
  color: white;
  
  svg {
    color: #4a90e2;
  }
  
  &:hover {
    background: rgba(74, 144, 226, 0.2);
    border-color: rgba(74, 144, 226, 0.3);
  }
`;

const MainContent = styled.div`
  display: grid;
  grid-template-columns: 300px 1fr;
  gap: 2.5rem;
  
  @media (max-width: 1200px) {
    gap: 2rem;
  }
  
  @media (max-width: 992px) {
    grid-template-columns: 1fr;
  }
`;

const ProductsSection = styled.div`
  display: flex;
  flex-direction: column;
`;

const ViewToggle = styled.div`
  display: flex;
  gap: 0.6rem;
  margin-left: auto;
  background: rgba(255, 255, 255, 0.05);
  padding: 0.4rem;
  border-radius: 10px;
  border: 1px solid rgba(255, 255, 255, 0.1);
`;

const ViewButton = styled(motion.button)`
  background: ${props => props.active ? 'rgba(74, 144, 226, 0.2)' : 'transparent'};
  color: ${props => props.active ? '#4a90e2' : 'rgba(255, 255, 255, 0.5)'};
  border: none;
  width: 38px;
  height: 38px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
  
  svg {
    font-size: 1.1rem;
  }
  
  &:hover {
    background: ${props => props.active ? 'rgba(74, 144, 226, 0.3)' : 'rgba(74, 144, 226, 0.1)'};
    color: ${props => props.active ? '#4a90e2' : 'rgba(255, 255, 255, 0.8)'};
  }
  
  @media (max-width: 768px) {
    width: 36px;
    height: 36px;
    
    svg {
      font-size: 1rem;
    }
  }
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: ${props => props.viewMode === 'grid' 
    ? 'repeat(auto-fill, minmax(280px, 1fr))' 
    : '1fr'};
  gap: 2.5rem;
  
  @media (max-width: 1200px) {
    grid-template-columns: ${props => props.viewMode === 'grid' 
      ? 'repeat(auto-fill, minmax(260px, 1fr))' 
      : '1fr'};
    gap: 2rem;
  }
  
  @media (max-width: 768px) {
    grid-template-columns: ${props => props.viewMode === 'grid' 
      ? 'repeat(auto-fill, minmax(240px, 1fr))' 
      : '1fr'};
    gap: 1.5rem;
  }
  
  @media (max-width: 480px) {
    grid-template-columns: 1fr;
    gap: 1.2rem;
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 4rem 2rem;
  color: rgba(255, 255, 255, 0.6);
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.05) 0%, rgba(255, 255, 255, 0.02) 100%);
  border-radius: 16px;
  width: 100%;
  border: 1px solid rgba(255, 255, 255, 0.07);
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.1);
  backdrop-filter: blur(10px);
  font-size: 1.1rem;
  line-height: 1.6;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 1rem;
  
  svg {
    font-size: 3rem;
    color: rgba(74, 144, 226, 0.5);
    margin-bottom: 1rem;
  }
  
  @media (max-width: 768px) {
    padding: 3rem 1.5rem;
    font-size: 1rem;
    
    svg {
      font-size: 2.5rem;
    }
  }
`;

const LoadingOverlay = styled.div`
  position: relative;
  width: 100%;
  height: 300px;
  display: flex;
  justify-content: center;
  align-items: center;
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.05) 0%, rgba(255, 255, 255, 0.02) 100%);
  border-radius: 16px;
  margin-bottom: 2rem;
  border: 1px solid rgba(255, 255, 255, 0.07);
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.1);
  backdrop-filter: blur(10px);
  flex-direction: column;
  gap: 1.5rem;
  
  @media (max-width: 768px) {
    height: 250px;
    margin-bottom: 1.5rem;
  }
`;

const LoadingSpinner = styled.div`
  width: 60px;
  height: 60px;
  border: 5px solid rgba(74, 144, 226, 0.1);
  border-radius: 50%;
  border-top-color: #4a90e2;
  border-left-color: #4a90e2;
  animation: spin 1.2s cubic-bezier(0.5, 0.1, 0.5, 1) infinite;
  box-shadow: 0 0 20px rgba(74, 144, 226, 0.2);
  
  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
  
  @media (max-width: 768px) {
    width: 50px;
    height: 50px;
    border-width: 4px;
  }
`;

const ProductList = () => {
  const [products, setProducts] = useState([]);
  const [artists, setArtists] = useState({});
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // 'all', 'current', or artistId
  const [searchQuery, setSearchQuery] = useState('');
  const [sortOrder, setSortOrder] = useState('desc'); // 'asc' or 'desc'
  const [error, setError] = useState(null);
  const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'list'
  const [advancedFilters, setAdvancedFilters] = useState(null);

  useEffect(() => {
    loadArtists();
  }, []);
  
  useEffect(() => {
    loadProducts();
  }, [filter, sortOrder, advancedFilters]);

  const loadArtists = async () => {
    try {
      const artistsRef = collection(db, collections.USERS);
      const q = query(artistsRef, where('roles.artist', '==', true));
      const querySnapshot = await getDocs(q);
      
      const artistsMap = {};
      querySnapshot.forEach(doc => {
        artistsMap[doc.id] = { ...doc.data(), $id: doc.id };
      });
      setArtists(artistsMap);
    } catch (error) {
      console.error('Error loading artists:', error);
      setError('Failed to load artist information');
    }
  };

  const loadProducts = async () => {
    try {
      setLoading(true);
      setError(null);
      const productsRef = collection(db, collections.PRODUCTS);
      let q;
      
      // Simplified query to avoid index errors
      if (filter === 'current') {
        // Instead of using a compound query, we'll fetch all active products and filter in memory
        q = query(
          productsRef, 
          where('status', '==', 'active')
        );
        
        const querySnapshot = await getDocs(q);
        const oneWeekAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000);
        
        let productsData = querySnapshot.docs.map(doc => ({
          ...doc.data(),
          $id: doc.id
        })).filter(product => {
          // Filter by creation date in memory
          const createdAt = product.createdAt?.toDate?.() || new Date();
          return createdAt >= oneWeekAgo;
        });
        
        // Sort in memory
        productsData.sort((a, b) => {
          const dateA = a.createdAt?.toDate?.() || new Date();
          const dateB = b.createdAt?.toDate?.() || new Date();
          return sortOrder === 'desc' ? dateB - dateA : dateA - dateB;
        });
        
        setProducts(productsData);
        setError(null);
        setLoading(false);
        return;
      } else if (filter !== 'all') {
        // Filter by artist ID
        q = query(
          productsRef,
          where('status', '==', 'active'),
          where('sellerId', '==', filter)
        );
      } else {
        // All products - just filter by status
        q = query(
          productsRef,
          where('status', '==', 'active')
        );
      }

      // Only execute this if we haven't already handled the 'current' filter case
      if (filter !== 'current') {
        const querySnapshot = await getDocs(q);
        let productsData = querySnapshot.docs.map(doc => ({
          ...doc.data(),
          $id: doc.id
        }));
        
        // Sort in memory to avoid index requirements
        productsData.sort((a, b) => {
          const dateA = a.createdAt?.toDate?.() || new Date();
          const dateB = b.createdAt?.toDate?.() || new Date();
          return sortOrder === 'desc' ? dateB - dateA : dateA - dateB;
        });
        
        // Apply advanced filters if any
        if (advancedFilters) {
          if (advancedFilters.minPrice) {
            productsData = productsData.filter(product => 
              parseFloat(product.price) >= parseFloat(advancedFilters.minPrice)
            );
          }
          
          if (advancedFilters.maxPrice) {
            productsData = productsData.filter(product => 
              parseFloat(product.price) <= parseFloat(advancedFilters.maxPrice)
            );
          }
          
          if (advancedFilters.categories && advancedFilters.categories.length > 0) {
            productsData = productsData.filter(product => 
              advancedFilters.categories.includes(product.category)
            );
          }
          
          if (advancedFilters.artists && advancedFilters.artists.length > 0) {
            productsData = productsData.filter(product => 
              advancedFilters.artists.includes(product.sellerId)
            );
          }
        }
        
        setProducts(productsData);
        setError(null);
      }
    } catch (error) {
      console.error('Error loading products:', error);
      // Don't show the error to users, just load empty products
      setProducts([]);
    } finally {
      setLoading(false);
    }
  };

  const toggleSortOrder = () => {
    setSortOrder(prev => prev === 'desc' ? 'asc' : 'desc');
  };

  const handleApplyFilters = (filters) => {
    setAdvancedFilters(filters);
  };

  const filteredProducts = products.filter(product => {
    if (!searchQuery) return true;
    
    const searchLower = searchQuery.toLowerCase();
    return (
      (product.name && product.name.toLowerCase().includes(searchLower)) ||
      (product.description && product.description.toLowerCase().includes(searchLower))
    );
  });

  return (
    <Container>
      <Header>
        <Title>KONZO Marketplace</Title>
        <Description>Discover unique products from your favorite artists</Description>
      </Header>
      
      <ControlsContainer>
        <SearchContainer>
          <SearchIcon>
            <FaSearch />
          </SearchIcon>
          <SearchInput 
            type="text" 
            placeholder="Search products..." 
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </SearchContainer>
        
        <FilterContainer>
          <FilterButton 
            active={filter === 'all'} 
            onClick={() => setFilter('all')}
            whileTap={{ scale: 0.95 }}
          >
            <FaFilter /> All Products
          </FilterButton>
          <FilterButton 
            active={filter === 'current'} 
            onClick={() => setFilter('current')}
            whileTap={{ scale: 0.95 }}
          >
            New Releases
          </FilterButton>
          {/* Artist filter buttons removed */}
          
          <SortButton
            onClick={toggleSortOrder}
            whileTap={{ scale: 0.95 }}
          >
            {sortOrder === 'desc' ? (
              <>
                <FaSortAmountDown /> Newest First
              </>
            ) : (
              <>
                <FaSortAmountUp /> Oldest First
              </>
            )}
          </SortButton>
        </FilterContainer>
      </ControlsContainer>

      <MainContent>
        <FilterPanel onApplyFilters={handleApplyFilters} />
        
        <ProductsSection>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '1rem' }}>
            <div style={{ color: 'white' }}>
              {!loading && !error && (
                <span>{filteredProducts.length} product{filteredProducts.length !== 1 ? 's' : ''} found</span>
              )}
            </div>
            <ViewToggle>
              <ViewButton 
                active={viewMode === 'grid'} 
                onClick={() => setViewMode('grid')}
                title="Grid View"
              >
                <FaThLarge />
              </ViewButton>
              <ViewButton 
                active={viewMode === 'list'} 
                onClick={() => setViewMode('list')}
                title="List View"
              >
                <FaList />
              </ViewButton>
            </ViewToggle>
          </div>
          
          {loading && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.5 }}
            >
              <LoadingOverlay>
                <LoadingSpinner />
                <div style={{ color: 'white', fontSize: '1.1rem', fontWeight: '500' }}>Loading products...</div>
              </LoadingOverlay>
            </motion.div>
          )}

          {error && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.5 }}
            >
              <EmptyState>
                <FaExclamationTriangle />
                <div>{error}</div>
              </EmptyState>
            </motion.div>
          )}

          {!loading && !error && filteredProducts.length === 0 && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.5 }}
            >
              <EmptyState>
                {searchQuery ? (
                  <>
                    <FaSearch />
                    <div>No products found matching <strong>"{searchQuery}"</strong></div>
                    <div style={{ fontSize: '0.9rem', opacity: 0.7, marginTop: '0.5rem' }}>Try using different keywords or browse all products</div>
                  </>
                ) : (
                  <>
                    <FaShoppingBag />
                    <div>No products available in this category</div>
                    <div style={{ fontSize: '0.9rem', opacity: 0.7, marginTop: '0.5rem' }}>Check back later or browse other categories</div>
                  </>
                )}
              </EmptyState>
            </motion.div>
          )}

          <Grid viewMode={viewMode}>
            <AnimatePresence>
              {filteredProducts.map(product => (
                <motion.div
                  key={product.$id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.3 }}
                >
                  <ProductCard
                    product={product}
                    artist={artists[product.sellerId]}
                    viewMode={viewMode}
                  />
                </motion.div>
              ))}
            </AnimatePresence>
          </Grid>
        </ProductsSection>
      </MainContent>
    </Container>
  );
};

export default ProductList;
